import type { ChatTokenTarget } from '@/features/live/types';

export interface LookupWorkspace {
  id: number;
}

export interface OwnerWorkspaceLookupInput {
  admin: boolean;
  webinarId: number | null;
  sessionId: number | null;
  currentWorkspaceId: number | null;
  tokenLoading: boolean;
  tokenError: string | null;
  /** Clé de la dernière recherche déjà jouée, `null` si aucune. */
  attemptedKey: string | null;
}

/** Le back répond 404 quand la session n'appartient pas au workspace interrogé. */
export function isSessionNotFoundError(error: string | null | undefined): boolean {
  if (!error) {
    return false;
  }
  return error.includes('404') || error.includes('NOT_FOUND') || error === 'errors.notFound';
}

export function ownerLookupKey(webinarId: number, sessionId: number): string {
  return `${webinarId}:${sessionId}`;
}

/**
 * Une recherche interroge un workspace après l'autre : sans ce verrou, l'erreur 404 encore en
 * mémoire pendant le refetch relance un balayage complet à chaque rendu.
 */
export function shouldLookUpOwnerWorkspace({
  admin,
  webinarId,
  sessionId,
  currentWorkspaceId,
  tokenLoading,
  tokenError,
  attemptedKey,
}: OwnerWorkspaceLookupInput): boolean {
  if (!admin || !webinarId || !sessionId || !currentWorkspaceId || tokenLoading) {
    return false;
  }
  if (!isSessionNotFoundError(tokenError)) {
    return false;
  }
  return attemptedKey !== ownerLookupKey(webinarId, sessionId);
}

/**
 * Le jeton en mémoire vaut pour une régie précise. La route de régie est la même pour toutes les
 * sessions : sans cette comparaison, une navigation interne réutilise le canal de la précédente.
 */
export function isChatTokenForTarget(
  target: ChatTokenTarget | null | undefined,
  current: { workspaceId: number | null; webinarId: number | null; sessionId: number | null },
): boolean {
  if (!target || !current.workspaceId || !current.webinarId || !current.sessionId) {
    return false;
  }
  return (
    target.workspaceId === current.workspaceId &&
    target.webinarId === current.webinarId &&
    target.sessionId === current.sessionId
  );
}

/** Le workspace actif vient d'échouer, le réinterroger ne ferait qu'un 404 de plus. */
export function pickLookupCandidates<T extends LookupWorkspace>(
  workspaces: T[] | null | undefined,
  currentWorkspaceId: number,
): T[] {
  return (workspaces ?? []).filter((workspace) => workspace.id !== currentWorkspaceId);
}

/**
 * Route de régie effectivement affichée, lien personnel compris. Toute écriture persistante qui
 * suit un `await` doit la comparer à celle qui l'a déclenchée : la navigation ne remonte pas
 * `AblyAuth`, donc rien n'annule une opération partie pour la route précédente.
 */
export function adminLiveRouteKey(
  webinarId: number | null,
  sessionId: number | null,
  staffLinkId: string | null,
): string | null {
  if (!webinarId || !sessionId) {
    return null;
  }
  return `${webinarId}:${sessionId}:${staffLinkId ?? ''}`;
}
